/* eslint-disable no-undef */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../dashboard/DashboardLayout';
import SettingsWizard from '../../components/SettingsWizard';
import { 
  getFormControlConfigs, 
  setFormControlConfigs, 
  checkExtensionStatus, 
  getLearnedFields, 
  clearLearnedFields, 
  dismissLearnedField 
} from '../../utils/extensionSync';
import { checkWizardCompletion } from '../../utils/wizardUtils';
import axiosInstance from '../../utils/axiosInstance';
import { 
  Sparkles, 
  CheckCircle2, 
  RotateCw, 
  Trash2, 
  Save, 
  Plus, 
  AlertTriangle, 
  Layers, 
  Type, 
  CheckSquare, 
  Check,
  ChevronDown, 
  Cpu, 
  Target, 
  Zap, 
  Database,
  ShieldCheck,
  Eye,
  Settings2,
  RefreshCw
} from 'lucide-react';
import { trackPageView, trackButtonClick } from '../../utils/analytics';

const FormControl = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [extensionStatus, setExtensionStatus] = useState({ available: false, synced: false });
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStatus, setWizardStatus] = useState({ completed: false });

  // Form control state
  const [defaultFields, setDefaultFields] = useState({ firstName: '', lastName: '', email: '', phone: '', location: '' });
  const [inputFieldConfigs, setInputFieldConfigs] = useState([]);
  const [radioButtons, setRadioButtons] = useState([]);
  const [dropdowns, setDropdowns] = useState([]);
  const [learnedFields, setLearnedFields] = useState([]);
  const [learnedLoading, setLearnedLoading] = useState(false);
  const [learnedDraft, setLearnedDraft] = useState({});

  // UI state for adding new items
  const [newInputPlaceholder, setNewInputPlaceholder] = useState('');
  const [newInputValue, setNewInputValue] = useState('');
  const [newRadioPlaceholder, setNewRadioPlaceholder] = useState('');
  const [newRadioValue, setNewRadioValue] = useState('');
  const [newDropdownPlaceholder, setNewDropdownPlaceholder] = useState('');
  const [newDropdownValue, setNewDropdownValue] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    trackPageView("/form-control", "Form Control Settings");
    loadConfigs();
    checkStatus();
    checkWizardStatus();
  }, []);

  const checkWizardStatus = async () => {
    try {
      const completion = await checkWizardCompletion();
      setWizardStatus(completion);
    } catch (e) {
      console.error('Failed to check wizard status:', e);
    }
  };

  const handleWizardComplete = () => {
    localStorage.setItem('applyease_wizard_completed', 'true');
    setShowWizard(false);
    checkWizardStatus();
    loadConfigs();
  };

  const checkStatus = async () => {
    const status = await checkExtensionStatus();
    setExtensionStatus(status);
  };

  const loadConfigs = async () => {
    setLoading(true);
    setError('');
    try {
      const configs = await getFormControlConfigs();
      setDefaultFields(configs.defaultFields || {});
      setInputFieldConfigs(configs.inputFieldConfigs || []);
      setRadioButtons(configs.radioButtons || []);
      setDropdowns(configs.dropdowns || []);

      try {
        const userData = await axiosInstance.get('/user');
        if (userData.data) {
          setDefaultFields(prev => ({
            firstName: userData.data.first_name || prev.firstName,
            lastName: userData.data.last_name || prev.lastName,
            email: userData.data.email || prev.email,
            phone: userData.data.phone || prev.phone,
            location: userData.data.location || prev.location,
          }));
        }
      } catch (e) {}

      try {
        setLearnedLoading(true);
        const learned = await getLearnedFields();
        setLearnedFields(Array.isArray(learned) ? learned : []);
      } catch {
        setLearnedFields([]);
      } finally {
        setLearnedLoading(false);
      }
    } catch (e) {
      setError('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const refreshLearnedFields = async () => {
    trackButtonClick("Refresh Detected Fields", "Form Control");
    setLearnedLoading(true);
    try {
      const learned = await getLearnedFields();
      setLearnedFields(Array.isArray(learned) ? learned : []);
    } catch (e) {
      setError('Failed to load detected fields');
    } finally {
      setLearnedLoading(false);
    }
  };

  const handleClearLearned = async () => {
    if (!window.confirm('Wipe detected intelligence profile?')) return;
    setLearnedLoading(true);
    try {
      await clearLearnedFields();
      setLearnedFields([]);
    } catch (e) {
      setError('Failed to clear');
    } finally {
      setLearnedLoading(false);
    }
  };

  const addLearnedAsConfig = async (field, kind) => {
    const placeholder = (field?.label || field?.placeholder || field?.name || '').trim();
    if (!placeholder) return;
    const fp = field?.fingerprint;
    const value = (learnedDraft?.[fp] || '').trim();

    if (kind === 'input') {
      if (!value) return;
      if (!inputFieldConfigs.some(c => c.placeholderIncludes === placeholder)) {
        setInputFieldConfigs(prev => ([...prev, { placeholderIncludes: placeholder, defaultValue: value, count: 1, createdAt: Date.now() }]));
      }
    } else if (kind === 'radio') {
      if (!value) return;
      if (!radioButtons.some(r => r.placeholderIncludes === placeholder)) {
        setRadioButtons(prev => ([...prev, { placeholderIncludes: placeholder, defaultValue: value, createdAt: Date.now() }]));
      }
    } else if (kind === 'select') {
      if (!value) return;
      if (!dropdowns.some(d => d.placeholderIncludes === placeholder)) {
        setDropdowns(prev => ([...prev, { placeholderIncludes: placeholder, value, createdAt: Date.now() }]));
      }
    }

    try { if (fp) await dismissLearnedField(fp); } catch {}
    setLearnedFields(prev => prev.filter((it) => it.fingerprint !== fp));
    setLearnedDraft(prev => { const next = { ...prev }; delete next[fp]; return next; });
    setSuccess('Intelligence node added to protocol.');
    setTimeout(() => setSuccess(''), 2500);
  };

  const saveConfigs = async () => {
    trackButtonClick("Save All Configurations", "Form Control");
    setSaving(true); setError(''); setSuccess('');
    try {
      const configs = { defaultFields, inputFieldConfigs, radioButtons, dropdowns };
      
      // Save to Extension
      await setFormControlConfigs(configs);
      
      // Save to Backend
      try {
        const formData = new FormData();
        formData.append('first_name', defaultFields.firstName);
        formData.append('last_name', defaultFields.lastName);
        formData.append('email', defaultFields.email);
        formData.append('phone', defaultFields.phone);
        formData.append('location', defaultFields.location);
        await axiosInstance.patch('/user', formData);
      } catch (e) {
        console.error("Backend sync failed:", e);
      }

      setSuccess('Settings synchronized.');
      setSaveSuccess(true);
      setTimeout(() => {
        setSuccess('');
        setSaveSuccess(false);
      }, 3000);
      await checkStatus();
    } catch (e) { 
      setError('Sync failure.'); 
    } finally { 
      setSaving(false); 
    }
  };

  const addInputField = () => {
    if (!newInputPlaceholder.trim() || !newInputValue.trim()) return;
    setInputFieldConfigs([...inputFieldConfigs, { placeholderIncludes: newInputPlaceholder.trim(), defaultValue: newInputValue.trim(), count: 1, createdAt: Date.now() }]);
    setNewInputPlaceholder(''); setNewInputValue('');
  };

  const addRadioButton = () => {
    if (!newRadioPlaceholder.trim() || !newRadioValue.trim()) return;
    setRadioButtons([...radioButtons, { placeholderIncludes: newRadioPlaceholder.trim(), defaultValue: newRadioValue.trim(), createdAt: Date.now() }]);
    setNewRadioPlaceholder(''); setNewRadioValue('');
  };

  const addDropdown = () => {
    if (!newDropdownPlaceholder.trim() || !newDropdownValue.trim()) return;
    setDropdowns([...dropdowns, { placeholderIncludes: newDropdownPlaceholder.trim(), value: newDropdownValue.trim(), createdAt: Date.now() }]);
    setNewDropdownPlaceholder(''); setNewDropdownValue('');
  };

  if (showWizard) {
    return (
      <DashboardLayout activeTab="form-control">
        <SettingsWizard onComplete={handleWizardComplete} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout activeTab="form-control" setTab={(t) => navigate('/dashboard', { state: { tab: t } })}>
      <div className="max-w-7xl mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20 px-4 md:px-0 font-sans">
        
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
           <div className="flex flex-col text-center md:text-left">
              <h2 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight italic flex items-center justify-center md:justify-start gap-3">
                 <Settings2 size={24} className="text-blue-600 sm:w-7 sm:h-7" /> Form Control
              </h2>
              <div className="flex items-center justify-center md:justify-start gap-3 mt-1">
                 <div className="flex items-center gap-1.5">
                    <div className={`w-1.5 h-1.5 rounded-full ${extensionStatus.available ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">
                       {extensionStatus.available ? 'Extension Active' : 'Extension Offline'}
                    </span>
                 </div>
              </div>
           </div>

           <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
              <button
                onClick={saveConfigs}
                disabled={saving}
                className={`px-8 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 italic active:scale-95 shadow-xl ${
                  saveSuccess 
                    ? 'bg-emerald-500 text-white shadow-emerald-500/20' 
                    : 'bg-slate-900 text-white hover:bg-black shadow-slate-900/20'
                }`}
              >
                {saving ? <RefreshCw size={16} className="animate-spin" /> : saveSuccess ? <Check size={16} /> : <Save size={16} />}
                {saving ? 'Syncing...' : saveSuccess ? 'Saved' : 'Save Changes'}
              </button>

              <button
                onClick={() => setShowWizard(true)}
                className="px-8 py-3 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-black hover:shadow-2xl hover:shadow-slate-200 transition-all flex items-center gap-2 italic active:scale-95 shadow-xl shadow-slate-100"
              >
                <Sparkles size={16} className="fill-white" /> Launch Setup Assistant
              </button>
           </div>
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-700 font-bold animate-in slide-in-from-top-2">
             <AlertTriangle size={18} /> <span className="text-sm">{error}</span>
          </div>
        )}
        {success && (
          <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-2xl flex items-center gap-3 text-emerald-700 font-bold animate-in slide-in-from-top-2">
             <CheckCircle2 size={18} /> <span className="text-sm">{success}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
           {/* 
           {/* Primary Configuration */}
           {/* <div className="lg:col-span-12">
              <div className="bg-slate-900 rounded-[3rem] p-10 text-white shadow-2xl relative overflow-hidden group border border-slate-800">
                 {/* <div className="absolute top-0 right-0 p-12 opacity-10 pointer-events-none -rotate-12 group-hover:rotate-0 transition-transform duration-1000">
                    <Database size={180} className="text-blue-500" />
                 </div> */}
                 
                 {/* <div className="relative z-10">
                    <div className="flex items-center justify-between mb-10">
                       <div>
                          <h3 className="text-2xl font-black italic tracking-tight mb-1">Detected Fields</h3>
                          <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest">Auto-Captured Form Fields</p>
                       </div>
                       <div className="flex gap-3">
                          <button onClick={refreshLearnedFields} className="p-3 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 transition-all text-blue-400"><RefreshCw size={20} className={learnedLoading ? 'animate-spin' : ''}/></button>
                          <button onClick={handleClearLearned} className="p-3 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 transition-all text-rose-400"><Trash2 size={20}/></button>
                       </div>
                    </div>

                    {learnedFields.length === 0 ? (
                       <div className="py-20 flex flex-col items-center justify-center border-2 border-dashed border-white/5 rounded-[2.5rem] bg-white/5">
                          <Eye size={48} className="text-white/10 mb-4" />
                          <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">No Fields Captured Yet</p>
                       </div>
                    ) : (
                       <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 max-h-[500px] overflow-y-auto pr-4 scrollbar-thin">
                          {learnedFields.map((f) => {
                             const key = f.fingerprint;
                             const label = (f.label || f.placeholder || f.name || 'Unknown Node').trim();
                             const kind = f.kind || 'input';
                             return (
                                <div key={key} className="bg-white/5 border border-white/10 rounded-3xl p-6 hover:bg-white/10 transition-all group overflow-hidden relative">
                                   <div className="absolute top-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity"><Zap size={16} className="text-blue-500 fill-blue-500"/></div>
                                   <div className="flex items-center gap-3 mb-4">
                                      <div className="w-10 h-10 bg-blue-600/10 text-blue-400 rounded-xl flex items-center justify-center shadow-inner"><Target size={18}/></div>
                                      <div className="min-w-0">
                                         <div className="text-xs font-black italic truncate pr-4">{label.toUpperCase()}</div>
                                         <div className="text-[9px] font-black text-slate-500 uppercase tracking-widest mt-0.5">{kind} protocol</div>
                                      </div>
                                   </div>
                                   <div className="flex gap-2">
                                      <input 
                                         className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-[10px] font-bold text-white outline-none focus:bg-white/10 focus:border-blue-500/50 transition-all italic" 
                                         placeholder="Value..."
                                         value={learnedDraft?.[key] || ''}
                                         onChange={(e) => setLearnedDraft((prev) => ({ ...prev, [key]: e.target.value }))}
                                      />
                                      <button 
                                         onClick={() => addLearnedAsConfig(f, kind === 'select' ? 'select' : kind === 'radio' ? 'radio' : 'input')}
                                         className="px-4 py-2 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 transition-all shadow-xl shadow-blue-500/10 active:scale-95"
                                      >
                                         Integrate
                                      </button>
                                   </div>
                                </div>
                             )
                          })}
                       </div>
                    )}
                 </div> 
              </div>
           </div> 
*/}

           {/* Presets Columns */}
           <div className="lg:col-span-5 space-y-10">
              <div className="bg-white border border-slate-200 rounded-[2rem] md:rounded-[3rem] p-5 md:p-8 shadow-sm relative overflow-hidden group">
                 <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none -rotate-12"><CheckSquare size={120} /></div>
                 
                 <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-8 flex items-center gap-2">
                    <ShieldCheck size={18} className="text-emerald-500" /> Default Profile
                 </h3>

                 <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                       <div className="space-y-1.5">
                          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">First Name</label>
                          <input className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-base md:text-sm font-black outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-400 transition-all shadow-inner italic" value={defaultFields.firstName} onChange={(e) => setDefaultFields({ ...defaultFields, firstName: e.target.value })} />
                       </div>
                       <div className="space-y-1.5">
                          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Last Name</label>
                          <input className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-black outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-400 transition-all shadow-inner italic" value={defaultFields.lastName} onChange={(e) => setDefaultFields({ ...defaultFields, lastName: e.target.value })} />
                       </div>
                    </div>
                    <div className="space-y-1.5">
                       <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Email</label>
                       <input className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-black outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-400 transition-all shadow-inner italic" type="email" value={defaultFields.email} onChange={(e) => setDefaultFields({ ...defaultFields, email: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                       <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Phone</label>
                       <input className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-black outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-400 transition-all shadow-inner italic" value={defaultFields.phone} onChange={(e) => setDefaultFields({ ...defaultFields, phone: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                       <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Location</label>
                       <input className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-black outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-400 transition-all shadow-inner italic" value={defaultFields.location} onChange={(e) => setDefaultFields({ ...defaultFields, location: e.target.value })} />
                    </div>
                 </div>
              </div>
           </div>

           <div className="lg:col-span-7 space-y-10">
              {/* Type-Specific Overrides */}
              {[
                { title: 'Text Field Rules', icon: Type, color: 'text-blue-500', data: inputFieldConfigs, setter: setInputFieldConfigs, add: addInputField, p1: newInputPlaceholder, setP1: setNewInputPlaceholder, p2: newInputValue, setP2: setNewInputValue, ph1: 'Placeholder Tag', ph2: 'Target Value' },
                { title: 'Radio Selection Logic', icon: CheckCircle2, color: 'text-emerald-500', data: radioButtons, setter: setRadioButtons, add: addRadioButton, p1: newRadioPlaceholder, setP1: setNewRadioPlaceholder, p2: newRadioValue, setP2: setNewRadioValue, ph1: 'Question Trigger', ph2: 'Select Value' },
                { title: 'Dropdown Selection Nodes', icon: ChevronDown, color: 'text-amber-500', data: dropdowns, setter: setDropdowns, add: addDropdown, p1: newDropdownPlaceholder, setP1: setNewDropdownPlaceholder, p2: newDropdownValue, setP2: setNewDropdownValue, ph1: 'Dropdown Label', ph2: 'Select Value' }
              ].map((section, si) => (
                <div key={si} className="bg-white border border-slate-200 rounded-[2rem] md:rounded-[3rem] p-5 md:p-8 shadow-sm relative overflow-hidden group">
                   <div className="flex items-center justify-between mb-8">
                      <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                         <section.icon size={18} className={section.color} /> {section.title}
                      </h3>
                      <div className="px-3 py-1 bg-slate-50 rounded-lg text-[10px] font-black text-slate-400">{section.data.length} Nodes</div>
                   </div>

                   <div className="bg-slate-50 p-6 rounded-[2rem] border border-slate-100 mb-8 flex flex-col md:flex-row gap-4">
                      <input className="flex-1 px-5 py-3 bg-white border border-slate-200 rounded-xl text-base md:text-[10px] font-black outline-none focus:border-blue-500 transition-all italic" placeholder={section.ph1} value={section.p1} onChange={(e) => section.setP1(e.target.value)} />
                      <input className="flex-1 px-5 py-3 bg-white border border-slate-200 rounded-xl text-base md:text-[10px] font-black outline-none focus:border-blue-500 transition-all italic" placeholder={section.ph2} value={section.p2} onChange={(e) => section.setP2(e.target.value)} />
                      <button className="px-5 bg-slate-900 text-white rounded-xl hover:bg-black transition-all shadow-lg active:scale-95" onClick={section.add}><Plus size={18}/></button>
                   </div>

                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[300px] overflow-y-auto pr-2 scrollbar-thin">
                      {section.data.map((config, idx) => (
                         <div key={idx} className="flex justify-between items-center p-5 bg-white border border-slate-100 rounded-2xl hover:border-blue-300 transition-all group/item shadow-sm">
                            <div className="min-w-0">
                               <div className="text-[10px] font-black text-slate-900 italic truncate uppercase">{config.placeholderIncludes}</div>
                               <div className="text-[10px] font-bold text-blue-500 truncate mt-1">➔ {config.defaultValue || config.value}</div>
                            </div>
                            <button onClick={() => section.setter(prev => prev.filter((_, i) => i !== idx))} className="p-2 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all opacity-0 group-hover/item:opacity-100"><Trash2 size={16}/></button>
                         </div>
                      ))}
                      {section.data.length === 0 && <div className="text-[10px] font-black text-slate-300 uppercase tracking-widest text-center py-10 italic">No overrides configured</div>}
                   </div>
                </div>
              ))}
           </div>

        </div>
      </div>
    </DashboardLayout>
  );
};

export default FormControl;
